import { nanoid } from 'nanoid';
import type { ReadonlyDeep } from 'type-fest';

import type { Locale } from '@change-corgi/config/locales';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { HomepageData, type HomepageDataQuery, type HomepageDataQueryVariables } from './api.graphql';
import { HomepagePageDataError } from './types';

export type HomepagePageData = ReadonlyDeep<{
	totalUserCount: HomepageDataQuery['totalUserCount'];
	topImpactStories: HomepageDataQuery['topImpactStories'];
	tips: ReadonlyArray<{
		title: string;
		guideLink: string;
	}>;
	aiSapId: string;
}>;

export async function getHomepagePageData(
	utilityContext: UtilityContext,
	{ locale }: { locale: string },
): Promise<HomepagePageData> {
	const {
		gql: { fetch },
		errorReporter,
	} = utilityContext;
	const { data } = await fetch<HomepageDataQuery, HomepageDataQueryVariables>({
		query: HomepageData,
		variables: {},
		rejectOnError: false,
	});

	if (!data) {
		// No data returned
		const error = new HomepagePageDataError('homepage_page_data_not_found');
		void errorReporter.report(error);
		throw error;
	}

	if (!data.featureConfigs.capybaraHomepageV3) {
		// No featureConfigs data
		const error = new HomepagePageDataError('homepage_page_data_not_found', {
			message: 'No featureConfigs data',
		});
		void errorReporter.report(error);
		throw error;
	}

	const {
		featureConfigs: { capybaraHomepageV3 },
	} = data;
	const localeConfig = capybaraHomepageV3.perLocaleConfig?.[locale as Locale];

	return {
		totalUserCount: data.totalUserCount,
		topImpactStories: data.topImpactStories,
		tips: localeConfig?.tips || [],
		aiSapId: nanoid(),
	};
}
